import { LayerLayoutData } from '../../components/Layer.types';
import { XY } from './startDrag';

// Common ===================

export function clamp(min: number, n: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function roundFixed10(n: number): number {
  return parseFloat(n.toFixed(10));
}

function rotateVectors(vectors: XY[], degClockwise: number): XY[] {
  const rad = -degClockwise * (Math.PI / 180);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  return vectors.map(vector => {
    const { x, y } = vector;
    return {
      x: roundFixed10(x * cos - y * sin),
      y: roundFixed10(x * sin + y * cos),
    };
  });
}

export function rotateVector(vector: XY, degClockwise: number): XY {
  return rotateVectors([vector], degClockwise)[0];
}

function rotateVectorOrigin(vector: XY, degClockwise: number, origin: XY = { x: 0, y: 0 }): [number, number] {
  const { x, y } = vector;
  const tx = x - origin.x;
  const ty = y - origin.y;
  const rad = -degClockwise * (Math.PI / 180);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return [roundFixed10(tx * cos - ty * sin) + origin.x, roundFixed10(tx * sin + ty * cos) + origin.y];
}

// Resize ===================

function ifPositiveThen(n: number, then: number) {
  return n > 0 ? then : n;
}

// Simplified version of the determinant method where:
// 1) the first line is parallel to the x-axis
// 2) we run this parallel line twice at y=0 and y=y1
// 3) only care about the x value of intersection
// 4) only care about intersections to the left of 0 (aka negative)
function intersection(y1: number, p1: XY, p2: XY): number {
  const denom = p2.y - p1.y;
  if (denom === 0) return Number.NEGATIVE_INFINITY; // Lines are parallel
  return Math.max(
    ifPositiveThen((p1.x * p2.y - p1.y * p2.x) / denom, Number.NEGATIVE_INFINITY),
    ifPositiveThen((-y1 * (p1.x - p2.x) + (p1.x * p2.y - p1.y * p2.x)) / denom, Number.NEGATIVE_INFINITY),
  );
}

function getLimit(y2: number, bounds: [XY, XY, XY, XY]) {
  const [topLeft, topRight, botRight, botLeft] = bounds;
  return Math.max(intersection(y2, topLeft, topRight), intersection(y2, topRight, botRight), intersection(y2, botRight, botLeft), intersection(y2, botLeft, topLeft));
}

// Returns 4 points representing the rotated bounds relative to the origin
// prettier-ignore
function getRotatedCorners(origin: XY, boundsWidth: number, boundsHeight: number, boundsMargin: number, rotation: number) {
  return [
    { x: 0           + boundsMargin - origin.x, y: 0            + boundsMargin - origin.y },
    { x: boundsWidth - boundsMargin - origin.x, y: 0            + boundsMargin - origin.y },
    { x: boundsWidth - boundsMargin - origin.x, y: boundsHeight - boundsMargin - origin.y },
    { x: 0           + boundsMargin - origin.x, y: boundsHeight - boundsMargin - origin.y }
  ].map(corner => rotateVector(corner, rotation)) as [XY, XY, XY, XY];
}

// top-l corner: page-rel:         rotation: applied, but technically doesn't matter since tl is origin
// bot-r corner: flipped-page-rel, rotation: applied
export function getLimits(pageWidth: number, pageHeight: number, pageMargin: number, layout: LayerLayoutData): [number, number] {
  const { x, y, width, height, rotation = 0 } = layout;
  const [tx, ty] = rotateVectorOrigin({ x: x + width, y: y + height }, -rotation, { x, y });
  const tl = { x, y };
  const br = { x: pageWidth - tx, y: pageHeight - ty };
  return [
    getLimit(layout.height, getRotatedCorners(tl, pageWidth, pageHeight, pageMargin, rotation)),
    getLimit(layout.height, getRotatedCorners(br, pageWidth, pageHeight, pageMargin, rotation)),
  ];
}

// Move =====================

export function getRotatedBounds(layout: LayerLayoutData) {
  const { width, height } = layout;
  const corners = rotateVectors(
    [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ],
    -(layout.rotation ?? 0),
  );

  const xs = corners.map(p => p.x);
  const ys = corners.map(p => p.y);

  const x1 = Math.min(...xs);
  const x2 = Math.max(...xs);
  const y1 = Math.min(...ys);
  const y2 = Math.max(...ys);

  return {
    x: x1,
    y: y1,
    width: x2 - x1,
    height: y2 - y1,
  };
}
